// Wire types — mirror the ac-admin Worker responses and the control-plane DB
// row shapes EXACTLY (snake_case as returned by D1). See:
//   workers/ac-admin/src/index.ts
//   packages/ac-control-plane-db/src/rows.ts
//   packages/ac-control-plane-db/src/contracts.ts

export type TenantStatus =
  | "provisioning"
  | "active"
  | "suspended"
  | "deprovisioning"
  | "deleted"
  | "failed";

export interface PlanRow {
  id: string;
  code: string;
  name: string;
  limits_json: string;
  features_json: string;
  active: number;
  created_at: number;
}

export interface TenantRow {
  id: string;
  slug: string;
  name: string;
  status: TenantStatus;
  plan_id: string | null;
  d1_database_id: string | null;
  d1_database_name: string | null;
  r2_bucket: string | null;
  kv_settings_prefix: string | null;
  primary_host: string | null;
  inbound_email: string | null;
  account_ref: string;
  region: string;
  created_at: number;
  updated_at: number;
  // Sales pipeline (0007). A LEAD has sales_stage set and no d1_database_id.
  sales_stage: string | null;
  contact_name: string | null;
  contact_email: string | null;
  lead_source: string | null;
  lead_notes: string | null;
}

/** Ordered sales-pipeline stages (mirrors the control plane). */
export const SALES_STAGES = [
  "new",
  "contacted",
  "demo",
  "contract_sent",
  "signed",
  "provisioned",
  "won",
  "lost",
] as const;
export type SalesStage = (typeof SALES_STAGES)[number];

/** A tenant is a lead while it has a sales stage but no provisioned database. */
export function isLead(t: Pick<TenantRow, "sales_stage" | "d1_database_id">): boolean {
  return !!t.sales_stage && !t.d1_database_id;
}

export interface CreateLeadInput {
  slug: string;
  name: string;
  planCode?: string;
  contactName?: string;
  contactEmail?: string;
  source?: string;
  notes?: string;
}

export interface TenantDomainRow {
  id: string;
  tenant_id: string;
  hostname: string;
  kind: "subdomain" | "custom";
  surface: "app" | "portal";
  verified: number;
  created_at: number;
}

export interface AdminAuditRow {
  id: string;
  actor_id: string | null;
  actor_email: string | null;
  action: string;
  tenant_id: string | null;
  before_json: string | null;
  after_json: string | null;
  ip: string | null;
  created_at: number;
}

export type ContractStatus =
  | "draft"
  | "sent"
  | "viewed"
  | "signed"
  | "declined"
  | "void"
  | "expired";

export interface ContractRow {
  id: string;
  tenant_id: string;
  template_id: string | null;
  title: string;
  body_html: string;
  document_hash: string;
  status: ContractStatus;
  plan_id: string | null;
  term_months: number | null;
  amount_cents: number | null;
  currency: string;
  version: number;
  created_by: string | null;
  created_at: number;
  sent_at: number | null;
  signed_at: number | null;
  expires_at: number | null;
}

export interface ContractSignerRow {
  id: string;
  contract_id: string;
  name: string;
  email: string;
  signing_order: number;
  token_hash: string;
  status: "pending" | "viewed" | "signed" | "declined";
  signature_type: "typed" | "drawn" | null;
  signature_text: string | null;
  signature_r2_key: string | null;
  consent_text: string | null;
  signed_at: number | null;
  signed_ip: string | null;
  signed_ua: string | null;
  expires_at: number;
  created_at: number;
}

// ----- response envelopes (Worker wraps payloads in named keys) -----

export interface HealthResponse {
  ok: boolean;
  service: string;
}

export interface PlansResponse {
  plans: PlanRow[];
}

export interface TenantsResponse {
  tenants: TenantRow[];
}

export interface TenantDetailResponse {
  tenant: TenantRow;
  domains: TenantDomainRow[];
  audit: AdminAuditRow[];
}

export interface AuditResponse {
  audit: AdminAuditRow[];
}

export interface ContractsResponse {
  contracts: ContractRow[];
}

export interface ContractDetailResponse {
  contract: ContractRow;
  signers: ContractSignerRow[];
}

// ----- provisioning state machine (control-plane jobs + steps) -----

export type ProvisioningJobStatus = "queued" | "running" | "succeeded" | "failed";
export type ProvisioningStepStatus = "pending" | "done" | "failed" | "skipped";

export interface ProvisioningJobRow {
  id: string;
  tenant_id: string;
  kind: "provision" | "deprovision";
  status: ProvisioningJobStatus;
  attempts: number;
  last_error: string | null;
  created_at: number;
  updated_at: number;
}

export interface ProvisioningStepRow {
  id: string;
  job_id: string;
  tenant_id: string;
  step: string;
  status: ProvisioningStepStatus;
  idempotency_key: string;
  result_json: string | null;
  error: string | null;
  started_at: number | null;
  finished_at: number | null;
}

/** GET /tenants/:id/health — latest job + its steps + human-readable issues. */
export interface TenantHealthResponse {
  tenant: TenantRow;
  latest_job: ProvisioningJobRow | null;
  steps: ProvisioningStepRow[];
  open_issues: string[];
}

// ----- fleet metrics (GET /metrics/overview) -----

export interface SignupPoint {
  day: string; // YYYY-MM-DD (UTC)
  n: number;
}

export interface ExpiringTrialEntry {
  tenant_id: string;
  slug: string;
  name: string;
  trial_ends_at: number;
}

export interface RecentImpersonationEntry {
  id: string;
  tenant_id: string;
  slug: string;
  name: string;
  staff_email: string | null;
  reason: string;
  created_at: number;
  expires_at: number;
  revoked_at: number | null;
}

export interface RecentFailureEntry {
  job_id: string;
  tenant_id: string;
  kind: string;
  last_error: string | null;
  updated_at: number;
}

export interface StuckProvisioningEntry {
  tenant_id: string;
  slug: string;
  minutes_stuck: number;
}

export interface MetricsOverviewResponse {
  tenants_by_status: Record<TenantStatus, number>;
  jobs_by_status: Record<ProvisioningJobStatus, number>;
  recent_failures: RecentFailureEntry[];
  stuck_provisioning: StuckProvisioningEntry[];
  trials_expiring_soon: { tenant_id: string; trial_ends_at: number }[];
  generated_at: number;
  signups: SignupPoint[];
  contracts_by_status: Partial<Record<ContractStatus, number>>;
  expiring_trials: ExpiringTrialEntry[];
  recent_impersonation: RecentImpersonationEntry[];
}

// ----- fleet-wide contracts (GET /contracts) -----

export interface FleetContractRow {
  id: string;
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  title: string;
  status: ContractStatus;
  plan_id: string | null;
  term_months: number | null;
  amount_cents: number | null;
  currency: string;
  created_by: string | null;
  created_at: number;
  sent_at: number | null;
  signed_at: number | null;
  expires_at: number | null;
  signer_count: number;
  signed_count: number;
}

export interface FleetContractsResponse {
  contracts: FleetContractRow[];
}

/** POST /tenants result (from enqueueProvision). */
export interface CreateTenantResult {
  tenantId: string;
  jobId?: string;
  alreadyExisted?: boolean;
}

export interface CreateTenantInput {
  slug: string;
  name: string;
  planCode?: string;
  adminEmail: string;
  adminName: string;
}

/** Signing link surfaced once at issue time (raw token only returned here). */
export interface IssuedSigner {
  signerId: string;
  email: string;
  signUrl: string;
  /** True when the platform mailer emailed this signer their link directly. */
  emailed?: boolean;
}

export interface IssueContractResult {
  contract: ContractRow;
  signers: IssuedSigner[];
}

export interface IssueContractInput {
  title: string;
  bodyHtml: string;
  templateId?: string;
  planCode?: string;
  termMonths?: number;
  amountCents?: number;
  signers: { name: string; email: string }[];
}

/** A reusable agreement template (markdown body with {{placeholders}}). */
export interface ContractTemplateRow {
  id: string;
  code: string;
  name: string;
  body_md: string;
  created_by: string | null;
  created_at: number;
  updated_at: number;
}

export interface ActivateSubscriptionInput {
  planCode?: string;
  seats?: number;
  notes?: string;
}

export interface SetStatusResult {
  tenantId: string;
  status: TenantStatus;
}

// ----- impersonation -----

/** Access level for an impersonation grant. Backend defaults to read_write. */
export type ImpersonationScope = "read_only" | "read_write";
